import React, { useEffect } from 'react';

import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import {
  maybePlayAudioOnce,
  useMaybePlayAudioOnceOnMount,
} from 'features/app-takeover/utils/play-audio-once';
import Animated, {
  FadeIn,
  LayoutAnimationConfig,
  SlideInLeft,
  SlideInRight,
} from 'react-native-reanimated';

import { IPicker, IPickerAspect } from '@rbi-ctg/menu';
import { FullWidthAutoHeightImage } from 'components/ui/full-width-auto-height-image';
import { MenuObjectTypes } from 'enums/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep, UserSelectionModifierSlotMap } from 'state/product-wizard-v2/types';
import { primitive, spacing } from 'styles/constants';
import { pickerAspectOptHasResolution } from 'utils/menu';

import { ProductCustomization } from '../product-customization';

import { FADE_IN_ANIMATION_DURATION, SLIDE_IN_ANIMATION_DURATION } from './constants';
import { CloseConfirmationDialog } from './dialogs/close-confirmation-dialog';
import { ProductDetailHome } from './product-home-flow';
import { DEFAULT_SLOT_KEY } from './product-item-flow/constants';
import { ProductOrderReview } from './product-order-review';
import { ProductDetailFlowContent, productDetailFlowContainerStyles } from './styled';

export const ProductDetailFlow: React.FC = () => {
  const { currentStep, slotKeyToCustomize } = useProductWizardFlow();
  const {
    menuObject,
    userSelections,
    isBackNavigation,
    setIsBackNavigation,
  } = useProductWizardContext();

  const {
    backgroundColor: takeoverBg,
    backgroundImage,
    audio,
    additionalAudioSteps,
  } = useAppTakeoverPdp(menuObject?._id);

  useMaybePlayAudioOnceOnMount(audio);

  useEffect(() => {
    maybePlayAudioOnce((additionalAudioSteps as any)?.[currentStep]);
  }, [additionalAudioSteps, currentStep]);

  const SLOT = menuObject._type === MenuObjectTypes.ITEM ? DEFAULT_SLOT_KEY : slotKeyToCustomize;

  const [modifierSelections, setModifierSelections] = React.useState<UserSelectionModifierSlotMap>(
    userSelections.modifiers[SLOT]
  );

  const [selectedPickerAspects, setSelectedPickerAspects] = React.useState(
    userSelections.pickerAspects || {}
  );

  const pickerData = menuObject as IPicker;

  const getPreviousPickerSelectionIdentifiers = React.useCallback(
    (startPoint: number, endPoint: number) =>
      pickerData.pickerAspects
        .slice(startPoint, endPoint)
        .map(aspect => selectedPickerAspects?.[aspect._id]),
    [pickerData.pickerAspects, selectedPickerAspects]
  );

  const filteredPickerAspects = React.useMemo<IPickerAspect[]>(
    () =>
      (pickerData.pickerAspects ?? []).map((currentAspect, index) => {
        const resolvedOpts = currentAspect.pickerAspectOptions.filter(option =>
          pickerAspectOptHasResolution(
            option.identifier,
            getPreviousPickerSelectionIdentifiers(0, index),
            pickerData.options
          )
        );
        return {
          ...currentAspect,
          pickerAspectOptions: resolvedOpts,
        };
      }),
    [getPreviousPickerSelectionIdentifiers, pickerData.options, pickerData.pickerAspects]
  );

  const renderContentByWizardStep = React.useCallback(() => {
    switch (currentStep) {
      case ProductWizardStep.ProductEditOrder:
      case ProductWizardStep.ProductCustomization:
        return (
          <ProductCustomization
            currentStep={currentStep}
            filteredPickerAspects={filteredPickerAspects}
            getPreviousPickerSelectionIdentifiers={getPreviousPickerSelectionIdentifiers}
            selectedPickerAspects={selectedPickerAspects}
            setSelectedPickerAspects={setSelectedPickerAspects}
          />
        );
      case ProductWizardStep.ProductReview:
        return <ProductOrderReview modifierSelections={modifierSelections} />;
      default:
        return (
          <ProductDetailHome
            modifierSelections={modifierSelections}
            handleChangeModifierSelections={setModifierSelections}
            filteredPickerAspects={filteredPickerAspects}
            getPreviousPickerSelectionIdentifiers={getPreviousPickerSelectionIdentifiers}
            selectedPickerAspects={selectedPickerAspects}
            setSelectedPickerAspects={setSelectedPickerAspects}
          />
        );
    }
  }, [
    currentStep,
    filteredPickerAspects,
    getPreviousPickerSelectionIdentifiers,
    modifierSelections,
    selectedPickerAspects,
  ]);

  const productDetailFlowKey = React.useMemo(() => `${currentStep}-${slotKeyToCustomize}`, [
    currentStep,
    slotKeyToCustomize,
  ]);
  const productDetailFlowKeyRef = React.useRef(productDetailFlowKey);
  productDetailFlowKeyRef.current = productDetailFlowKey;

  // Cart-edit opens with query params that can settle after the first paint (and/or after
  // skeleton → content remount). That changes `productDetailFlowKey` while the old
  // `isMountedRef` was already true, so SlideIn* ran on what felt like "mount".
  // Keep step slides for real wizard navigation, but only after this screen has settled.
  const [stepAnimationsEnabled, setStepAnimationsEnabled] = React.useState(false);
  const previousFlowKeyRef = React.useRef(productDetailFlowKey);

  const isStepTransition =
    stepAnimationsEnabled && previousFlowKeyRef.current !== productDetailFlowKey;

  // Sync after paint so the render that sees a key change still gets entering={Slide*}.
  React.useLayoutEffect(() => {
    previousFlowKeyRef.current = productDetailFlowKey;
  }, [productDetailFlowKey]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      previousFlowKeyRef.current = productDetailFlowKeyRef.current;
      setStepAnimationsEnabled(true);
    }, SLIDE_IN_ANIMATION_DURATION);

    return () => clearTimeout(timer);
  }, []);

  React.useEffect(() => {
    return () => {
      setIsBackNavigation(false);
    };
  }, [productDetailFlowKey, setIsBackNavigation]);

  const Animation = isBackNavigation ? SlideInLeft : SlideInRight;

  return (
    <>
      {/* skipEntering is unreliable on web; isStepTransition gates mount vs step changes. */}
      <LayoutAnimationConfig skipEntering>
        <Animated.View
          key={productDetailFlowKey}
          style={productDetailFlowContainerStyles}
          entering={isStepTransition ? FadeIn.duration(FADE_IN_ANIMATION_DURATION) : undefined}
        >
          <Animated.View
            entering={
              isStepTransition ? Animation.duration(SLIDE_IN_ANIMATION_DURATION) : undefined
            }
            style={{
              ...productDetailFlowContainerStyles,
              backgroundColor: takeoverBg || primitive.$white,
            }}
          >
            <ProductDetailFlowContent testID="product-detail-flow-content">
              {backgroundImage && currentStep !== ProductWizardStep.ProductHome && (
                <FullWidthAutoHeightImage
                  source={backgroundImage}
                  style={{ position: 'absolute', top: spacing.$16 }}
                />
              )}
              {renderContentByWizardStep()}
            </ProductDetailFlowContent>
          </Animated.View>
        </Animated.View>
      </LayoutAnimationConfig>
      {/* Confirmation Dialogs */}
      <CloseConfirmationDialog />
    </>
  );
};
